import React, { useEffect } from 'lib/react';
import { useTranslation } from 'lib/translation';
import { useDispatch, useSelector } from 'lib/redux';
import { EVGrid } from 'lib/components';
import PropTypes from 'prop-types';
import Map from 'commonComponents/map';
import MapDisclaimer from 'commonComponents/map-disclaimer';
import ToastMessage from 'commonComponents/toast-message';
import { getMapZoomForPoint } from 'commonComponents/map/mapView.utils';
import { COUNTRY } from 'constants/AppConstants';
import history from 'store/CreateHistory';
import clsx from 'clsx';
import { isAuthorized } from 'utils/auth.utils';
import { isEntitledForEmbeddedExplorerSelector } from 'utils/appSelectors.utils';
import { gainsightPxCustomEvent } from 'utils/gainsightPx.utils';
import {
  GAINSIGHT_PX_EVENT_MAP_PIN_ADDED,
  GAINSIGHT_PX_EVENT_MAP_PIN_LOCATION_UPDATED,
  GAINSIGHT_PX_EVENT_MAP_PIN_DELETED,
} from 'constants/gainsightPxCustomEvents.constants';
import OrderStyles from './OrderStyles';
import PropertyDetailsPanel from './components/PropertyDetailsPanel.component';
import {
  closeMapToast, setCountryAndClearAddress,
  addMapPoint, fetchAddressAndResetInput, deleteMapPointAction, updateMapPointLocationAction, updateFocusedPointIndex,
  cancelOrderSelection,
  fetchPinAddress,
} from './Order.actions';

function AddressSearchOrder(props) {
  const { showMap, setShowMap } = props;
  const { t } = useTranslation();
  const {
    mapPoints, mapToastOpen, mapToastMessage,
    country, isComplex, propertyType, focusedPointIndex, isOrderFlowStarted, geocodedAddress,
  } = useSelector(({
    order, orderAddresses,
  }) => ({
    ...order,
    mapPoints: orderAddresses.mapPoints,
    geocodedAddress: orderAddresses.geocodedAddress,
    focusedPointIndex: orderAddresses.focusedPointIndex,
    isOrderFlowStarted: order.isOrderFlowStarted,
  }));

  const useEE = useSelector(({ order }) => order.useEmbeddedExplorer);

  const isEntitledForEmbeddedExplorer = useSelector(isEntitledForEmbeddedExplorerSelector);

  const classes = OrderStyles();
  const dispatch = useDispatch();

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const countryFromSearchParams = urlSearchParams.get(COUNTRY);

    if (countryFromSearchParams) {
      dispatch(setCountryAndClearAddress(countryFromSearchParams, false));
    }

    // Block transitions from order page to clear the selections for current order
    history.unblock = history.block((tx) => {
      dispatch(cancelOrderSelection());
      history?.unblock();
      history.push(tx.pathname);
    });

    return () => {
      history?.unblock();
      delete history?.unblock;
    };
  }, []);

  useEffect(() => {
    // Block transitions from order page to clear the selections for current order
    history.unblock = history.block((tx) => {
      dispatch(cancelOrderSelection());
      history?.unblock();
      history.push(tx.pathname);
    });

    return () => {
      if (history?.unblock) {
        history?.unblock();
        delete history?.unblock;
      }
    };
  }, [isOrderFlowStarted]);

  const handleToastClose = () => dispatch(closeMapToast());

  const handleAddPin = (latitude, longitude, pinCount) => {
    gainsightPxCustomEvent(GAINSIGHT_PX_EVENT_MAP_PIN_ADDED, { latitude, longitude, pinCount });
    if (pinCount === 0) {
      dispatch(fetchAddressAndResetInput(`${latitude}, ${longitude}`, country, true));
    } else dispatch(addMapPoint({ latitude, longitude }));
  };

  const handleUpdatePinLocation = (updateIndex, newPinLocation) => {
    gainsightPxCustomEvent(
      GAINSIGHT_PX_EVENT_MAP_PIN_LOCATION_UPDATED,
      {
        pinIndex: updateIndex,
        latitude: newPinLocation.latitude,
        longitude: newPinLocation.longitude,
      },
    );
    dispatch(updateMapPointLocationAction(updateIndex, newPinLocation));
  };

  const handleDeletePin = (deleteIndex) => {
    gainsightPxCustomEvent(GAINSIGHT_PX_EVENT_MAP_PIN_DELETED, { pinIndex: deleteIndex });
    dispatch(deleteMapPointAction(deleteIndex));
  };

  const handleFocusChange = (index) => dispatch(updateFocusedPointIndex(focusedPointIndex === index ? null : index));

  const handleFetchPinAddress = (pinIndex) => dispatch(fetchPinAddress(pinIndex));

  return (

    <EVGrid container className={clsx(classes.selectionContainer, !isAuthorized() && 'unauthorised')} wrap="nowrap">
      <EVGrid className={classes.mapContainer}>
        <ToastMessage
          alertMsg={t(mapToastMessage)}
          severity="info"
          open={mapToastOpen}
          onClose={handleToastClose}
        />
        <Map
          useEmbeddedExplorer={isEntitledForEmbeddedExplorer && useEE}
          mapPoints={mapPoints}
          mapOptions={((mapPoints?.length === 0) && geocodedAddress) ? { center: geocodedAddress } : {}}
          isComplex={isComplex}
          propertyType={propertyType}
          onAddPin={handleAddPin}
          onUpdatePinLocation={handleUpdatePinLocation}
          onDeletePin={handleDeletePin}
          onFocusChange={handleFocusChange}
          focusedPinIndex={focusedPointIndex}
          pinDragEnabled
          onFetchPinAddress={handleFetchPinAddress}
          defaultZoom={getMapZoomForPoint({
            usedZipFallback: mapPoints[0]?.usedZipFallback,
            userMovedThePin: mapPoints[0]?.userMovedThePin,
          })}
        />
        <MapDisclaimer />
      </EVGrid>
      <PropertyDetailsPanel showMap={showMap} setShowMap={setShowMap} />
    </EVGrid>
  );
}

AddressSearchOrder.propTypes = {
  showMap: PropTypes.bool.isRequired,
  setShowMap: PropTypes.func.isRequired,
};

export default AddressSearchOrder;
